/**
 * API client for Orientamos Hub
 */

import type { IntakeFormData, IntakeFilters, PartyFormData } from '@orientamos/shared'

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api`

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

/**
 * Token getter function, set via setAuthTokenGetter
 */
let _getToken: (() => Promise<string | null>) | null = null

/**
 * Initialize the API client with Clerk's getToken from useAuth()
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  _getToken = getter
}

/**
 * Get the auth token from Clerk
 */
async function getAuthToken(): Promise<string | null> {
  if (_getToken) {
    return _getToken()
  }
  return null
}

/**
 * Make an authenticated API request
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const token = await getAuthToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Merge in any existing headers
  if (options.headers) {
    const existingHeaders = options.headers as Record<string, string>
    Object.assign(headers, existingHeaders)
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    })

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || 'Error en la solicitud',
      }
    }

    return data as ApiResponse<T>
  } catch (error) {
    console.error('API request error:', error)
    return {
      success: false,
      error: 'Error de conexion',
    }
  }
}

// ===========================================
// Intake API
// ===========================================

export interface IntakeListItem {
  id: string
  orderNumber: string
  status: 'draft' | 'complete' | 'approved'
  contractType: 'residential' | 'commercial'
  guaranteedRent: boolean
  property: {
    address: string
    city: string
    propertyType: string
  }
  createdAt: string
  updatedAt: string
}

export interface IntakeDetail extends IntakeListItem {
  terms: {
    monthlyRent: number
    administrationFee: number
    startDate: string
    endDate: string
    durationMonths: number
    rentIncreaseFormula: string
  }
  insurance: {
    filingNumber?: string
    date?: string
    provider?: string
  } | null
  commission: {
    percentage: number
    comprehensiveCoverage: boolean
    utilitiesCoverage: boolean
    damagesCoverage: boolean
  }
  parties: PartyDetail[]
  capturedBy?: string
  rentedBy?: string
  createdBy: string
}

export interface PartyDetail {
  id: string
  intakeId: string
  role: 'tenant' | 'guarantor' | 'owner' | 'beneficiary'
  personType: 'natural' | 'legal'
  sortOrder: number
  companyName?: string
  nit?: string
  legalRepName?: string
  legalRepId?: string
  firstName: string
  lastName: string
  idNumber: string
  idIssuedAt?: string
  profession?: string
  phone: string
  address: string
  city: string
  email: string
  invoiceEmail?: string
  banking?: {
    accountNumber: string
    bankName: string
    accountType: 'ahorros' | 'corriente'
    accountHolderName: string
    accountHolderId: string
  }
  documents?: {
    idCopy?: string
    chamberOfCommerce?: string
    rut?: string
  }
  dataAuthorizationAccepted: boolean
  referenceCheckAuthorized: boolean
}

/**
 * List intakes with filters and pagination
 */
export async function listIntakes(
  filters: Partial<IntakeFilters> = {}
): Promise<PaginatedResponse<IntakeListItem>> {
  const params = new URLSearchParams()

  if (filters.status) params.set('status', filters.status)
  if (filters.contractType) params.set('contractType', filters.contractType)
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder)

  const queryString = params.toString()
  const endpoint = `/intakes${queryString ? `?${queryString}` : ''}`

  const response = await apiRequest<IntakeListItem[]>(endpoint)

  if (!response.success) {
    return {
      success: false,
      data: [],
      pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  }

  // The response already includes pagination from the API
  return response as unknown as PaginatedResponse<IntakeListItem>
}

/**
 * Get a single intake by ID
 */
export async function getIntake(id: string): Promise<ApiResponse<IntakeDetail>> {
  return apiRequest<IntakeDetail>(`/intakes/${id}`)
}

/**
 * Create a new intake
 */
export async function createIntake(
  data: IntakeFormData
): Promise<ApiResponse<{ id: string; orderNumber: string }>> {
  return apiRequest<{ id: string; orderNumber: string }>('/intakes', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update an intake
 */
export async function updateIntake(
  id: string,
  data: Partial<IntakeFormData>
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/intakes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete an intake
 */
export async function deleteIntake(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/intakes/${id}`, {
    method: 'DELETE',
  })
}

/**
 * Update intake status
 */
export async function updateIntakeStatus(
  id: string,
  status: 'draft' | 'complete' | 'approved'
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/intakes/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
}

// ===========================================
// Party API
// ===========================================

/**
 * Add a party to an intake
 */
export async function addParty(
  intakeId: string,
  party: PartyFormData
): Promise<ApiResponse<{ id: string }>> {
  return apiRequest<{ id: string }>(`/intakes/${intakeId}/parties`, {
    method: 'POST',
    body: JSON.stringify(party),
  })
}

/**
 * Update a party
 */
export async function updateParty(
  intakeId: string,
  partyId: string,
  data: Partial<PartyFormData>
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/intakes/${intakeId}/parties/${partyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete a party
 */
export async function deleteParty(intakeId: string, partyId: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/intakes/${intakeId}/parties/${partyId}`, {
    method: 'DELETE',
  })
}

// ===========================================
// Document API
// ===========================================

/**
 * Upload a document
 */
export async function uploadDocument(
  intakeId: string,
  file: File,
  documentType: 'idCopy' | 'chamberOfCommerce' | 'rut'
): Promise<ApiResponse<{ key: string }>> {
  // First, get the upload URL
  const urlResponse = await apiRequest<{ key: string; uploadUrl: string }>(
    `/intakes/${intakeId}/documents`,
    {
      method: 'POST',
      body: JSON.stringify({
        documentType,
        filename: file.name,
        contentType: file.type,
      }),
    }
  )

  if (!urlResponse.success || !urlResponse.data) {
    return { success: false, error: urlResponse.error }
  }

  // Then upload the file
  const token = await getAuthToken()
  const uploadResponse = await fetch(`${API_BASE}${urlResponse.data.uploadUrl}`, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: file,
  })

  if (!uploadResponse.ok) {
    return { success: false, error: 'Error al cargar el archivo' }
  }

  return { success: true, data: { key: urlResponse.data.key } }
}

/**
 * Get document download URL
 */
export function getDocumentUrl(intakeId: string, key: string): string {
  return `${API_BASE}/intakes/${intakeId}/documents/${encodeURIComponent(key)}`
}

// ===========================================
// AI Extraction API
// ===========================================

export interface AIExtractionResult {
  fields: {
    contractType?: 'residential' | 'commercial' | null
    property?: {
      address?: string | null
      city?: string | null
      stratum?: number | null
      area?: number | null
      propertyType?: string | null
    }
    tenant?: {
      firstName?: string | null
      lastName?: string | null
      idNumber?: string | null
      phone?: string | null
      email?: string | null
      address?: string | null
      city?: string | null
    }
    owner?: {
      firstName?: string | null
      lastName?: string | null
      idNumber?: string | null
      phone?: string | null
      email?: string | null
      address?: string | null
      city?: string | null
    }
    terms?: {
      monthlyRent?: number | null
      administrationFee?: number | null
      startDate?: string | null
      endDate?: string | null
      durationMonths?: number | null
    }
  }
  confidence: Record<string, number>
}

/**
 * Extract intake form fields from text using AI
 */
export async function extractIntakeFromText(
  text: string
): Promise<ApiResponse<AIExtractionResult>> {
  return apiRequest<AIExtractionResult>('/ai/extract-intake', {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
}

/**
 * Extract intake form fields from file content using AI
 */
export async function extractIntakeFromFile(
  content: string,
  filename: string,
  mimeType: string
): Promise<ApiResponse<AIExtractionResult>> {
  return apiRequest<AIExtractionResult>('/ai/extract-intake-file', {
    method: 'POST',
    body: JSON.stringify({ content, filename, mimeType }),
  })
}

/**
 * Template extraction result from AI
 */
export interface TemplateExtractionResult {
  contractType: 'residential' | 'commercial'
  name: string
  htmlContent: string
}

/**
 * Extract a reusable template with placeholders from contract text/HTML
 */
export async function extractTemplateFromText(
  text: string
): Promise<ApiResponse<TemplateExtractionResult>> {
  return apiRequest<TemplateExtractionResult>('/ai/extract-template', {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
}

/**
 * Check AI service status
 */
export async function getAIStatus(): Promise<
  ApiResponse<{ available: boolean; provider: string; model: string }>
> {
  return apiRequest<{ available: boolean; provider: string; model: string }>('/ai/status')
}

// ===========================================
// Template API
// ===========================================

export interface TemplateListItem {
  id: string
  name: string
  type: 'residential' | 'commercial'
  region: string
  version: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface TemplateField {
  key: string
  label: string
  type: 'text' | 'number' | 'date' | 'select' | 'textarea'
  required: boolean
  source?: string
  sourcePath?: string
  options?: string[]
  default?: string | number
}

export interface TemplateDetail extends TemplateListItem {
  htmlContent: string
  fields: TemplateField[]
  updatedBy: string
}

export interface TemplateFilters {
  type?: 'residential' | 'commercial'
  region?: string
  isActive?: boolean
  search?: string
  page?: number
  limit?: number
}

/**
 * List templates with filters and pagination
 */
export async function listTemplates(
  filters: TemplateFilters = {}
): Promise<PaginatedResponse<TemplateListItem>> {
  const params = new URLSearchParams()

  if (filters.type) params.set('type', filters.type)
  if (filters.region) params.set('region', filters.region)
  if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive))
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))

  const queryString = params.toString()
  const endpoint = `/templates${queryString ? `?${queryString}` : ''}`

  const response = await apiRequest<TemplateListItem[]>(endpoint)

  if (!response.success) {
    return {
      success: false,
      data: [],
      pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  }

  return response as unknown as PaginatedResponse<TemplateListItem>
}

/**
 * Get a single template by ID
 */
export async function getTemplate(id: string): Promise<ApiResponse<TemplateDetail>> {
  return apiRequest<TemplateDetail>(`/templates/${id}`)
}

/**
 * Create a new template
 */
export async function createTemplate(data: {
  name: string
  type: 'residential' | 'commercial'
  region: string
  htmlContent: string
  fields?: TemplateField[]
}): Promise<ApiResponse<{ id: string }>> {
  return apiRequest<{ id: string }>('/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update a template
 */
export async function updateTemplate(
  id: string,
  data: Partial<{
    name: string
    type: 'residential' | 'commercial'
    region: string
    htmlContent: string
    fields: TemplateField[]
  }>
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete (deactivate) a template
 */
export async function deleteTemplate(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/templates/${id}`, {
    method: 'DELETE',
  })
}

/**
 * Duplicate a template
 */
export async function duplicateTemplate(
  id: string
): Promise<ApiResponse<{ id: string; version: number }>> {
  return apiRequest<{ id: string; version: number }>(`/templates/${id}/duplicate`, {
    method: 'POST',
  })
}

// ===========================================
// Clause API
// ===========================================

export type ClauseCategory =
  | 'services'
  | 'grace_period'
  | 'rent_increase'
  | 'renewal'
  | 'pets'
  | 'modifications'
  | 'insurance'
  | 'utilities'
  | 'other'

export interface ClauseListItem {
  id: string
  title: string
  category: ClauseCategory
  description: string
  contractTypes: ('residential' | 'commercial')[]
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface ClauseDetail extends ClauseListItem {
  htmlContent: string
  fields: TemplateField[] | null
}

export interface ClauseFilters {
  category?: ClauseCategory
  contractType?: 'residential' | 'commercial'
  isActive?: boolean
  search?: string
  page?: number
  limit?: number
}

/**
 * List clauses with filters and pagination
 */
export async function listClauses(
  filters: ClauseFilters = {}
): Promise<PaginatedResponse<ClauseListItem>> {
  const params = new URLSearchParams()

  if (filters.category) params.set('category', filters.category)
  if (filters.contractType) params.set('contractType', filters.contractType)
  if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive))
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))

  const queryString = params.toString()
  const endpoint = `/clauses${queryString ? `?${queryString}` : ''}`

  const response = await apiRequest<ClauseListItem[]>(endpoint)

  if (!response.success) {
    return {
      success: false,
      data: [],
      pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
    }
  }

  return response as unknown as PaginatedResponse<ClauseListItem>
}

/**
 * Get a single clause by ID
 */
export async function getClause(id: string): Promise<ApiResponse<ClauseDetail>> {
  return apiRequest<ClauseDetail>(`/clauses/${id}`)
}

/**
 * Create a new clause
 */
export async function createClause(data: {
  title: string
  category: ClauseCategory
  htmlContent: string
  description: string
  contractTypes: ('residential' | 'commercial')[]
  fields?: TemplateField[]
}): Promise<ApiResponse<{ id: string }>> {
  return apiRequest<{ id: string }>('/clauses', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update a clause
 */
export async function updateClause(
  id: string,
  data: Partial<{
    title: string
    category: ClauseCategory
    htmlContent: string
    description: string
    contractTypes: ('residential' | 'commercial')[]
    fields: TemplateField[]
  }>
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/clauses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete (deactivate) a clause
 */
export async function deleteClause(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/clauses/${id}`, {
    method: 'DELETE',
  })
}

/**
 * Reorder clauses within a category
 */
export async function reorderClauses(
  category: ClauseCategory,
  clauseIds: string[]
): Promise<ApiResponse<void>> {
  return apiRequest<void>('/clauses/reorder', {
    method: 'PUT',
    body: JSON.stringify({ category, clauseIds }),
  })
}

// ===========================================
// Contract API
// ===========================================

export type ContractStatus = 'draft' | 'pending_review' | 'approved' | 'sent' | 'signed'

export interface ContractListItem {
  id: string
  intakeId: string
  templateId: string
  status: ContractStatus
  version: number
  orderNumber: string
  templateName: string
  propertyAddress: string
  tenantName: string
  createdAt: string
  updatedAt: string
  approvedAt: string | null
  sentAt: string | null
  signedAt: string | null
}

export interface ContractDetail extends ContractListItem {
  dataSnapshot: Record<string, unknown>
  selectedClauses: string[]
  fieldOverrides: Record<string, unknown>
  renderedHtml: string | null
  files: {
    pdf?: string
    docx?: string
  } | null
  approvedBy: string | null
  signatureStatus: 'pending' | 'sent' | 'signed' | 'declined' | 'expired' | null
  createdBy: string
}

export interface ContractFilters {
  status?: ContractStatus
  intakeId?: string
  templateId?: string
  search?: string
  page?: number
  limit?: number
  sortBy?: 'createdAt' | 'updatedAt' | 'status'
  sortOrder?: 'asc' | 'desc'
}

/**
 * List contracts with filters and pagination
 */
export async function listContracts(
  filters: ContractFilters = {}
): Promise<PaginatedResponse<ContractListItem>> {
  const params = new URLSearchParams()

  if (filters.status) params.set('status', filters.status)
  if (filters.intakeId) params.set('intakeId', filters.intakeId)
  if (filters.templateId) params.set('templateId', filters.templateId)
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder)

  const queryString = params.toString()
  const endpoint = `/contracts${queryString ? `?${queryString}` : ''}`

  const response = await apiRequest<ContractListItem[]>(endpoint)

  if (!response.success) {
    return {
      success: false,
      data: [],
      pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  }

  return response as unknown as PaginatedResponse<ContractListItem>
}

/**
 * Get a single contract by ID
 */
export async function getContract(id: string): Promise<ApiResponse<ContractDetail>> {
  return apiRequest<ContractDetail>(`/contracts/${id}`)
}

/**
 * Create a new contract from intake
 */
export async function createContract(data: {
  intakeId: string
  templateId: string
  selectedClauses?: string[]
  fieldOverrides?: Record<string, unknown>
}): Promise<ApiResponse<{ id: string }>> {
  return apiRequest<{ id: string }>('/contracts', {
    method: 'POST',
    body: JSON.stringify({
      intakeId: data.intakeId,
      templateId: data.templateId,
      selectedClauses: data.selectedClauses || [],
      fieldOverrides: data.fieldOverrides || {},
    }),
  })
}

/**
 * Update a contract
 */
export async function updateContract(
  id: string,
  data: {
    selectedClauses?: string[]
    fieldOverrides?: Record<string, unknown>
    status?: ContractStatus
  }
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/contracts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Re-render contract HTML
 */
export async function renderContract(id: string): Promise<ApiResponse<{ renderedHtml: string }>> {
  return apiRequest<{ renderedHtml: string }>(`/contracts/${id}/render`, {
    method: 'POST',
  })
}

/**
 * Approve a contract
 */
export async function approveContract(id: string, notes?: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/contracts/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  })
}

/**
 * Mark contract as sent
 */
export async function sendContract(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/contracts/${id}/send`, {
    method: 'POST',
  })
}

/**
 * Get contract HTML for export (PDF or DOCX)
 */
export async function exportContractHtml(
  id: string
): Promise<ApiResponse<{ html: string; status: ContractStatus }>> {
  return apiRequest<{ html: string; status: ContractStatus }>(`/contracts/${id}/export/pdf`, {
    method: 'POST',
  })
}

/** @deprecated Use exportContractHtml instead */
export const exportContractPdf = exportContractHtml

/**
 * Generate and download a PDF for a contract (server-side rendering)
 */
export async function exportContractPdfBlob(id: string): Promise<ApiResponse<Blob>> {
  const token = await getAuthToken()

  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  try {
    const response = await fetch(`${API_BASE}/contracts/${id}/export/pdf/download`, {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: 'Error al generar PDF' }))
      return { success: false, error: data.error || 'Error al generar PDF' }
    }

    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    console.error('PDF export error:', error)
    return { success: false, error: 'Error de conexion al generar PDF' }
  }
}

/**
 * Store PDF/DOCX file reference
 */
export async function storeContractFile(
  id: string,
  type: 'pdf' | 'docx',
  key: string
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/contracts/${id}/files`, {
    method: 'POST',
    body: JSON.stringify({ type, key }),
  })
}

/**
 * Delete a contract
 */
export async function deleteContract(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/contracts/${id}`, {
    method: 'DELETE',
  })
}

/**
 * Send contract via email
 */
export interface SendContractEmailData {
  to: string[]
  cc?: string[]
  subject: string
  message?: string
}

export interface SendContractEmailResult {
  messageId: string
  sentAt: string
}

export async function sendContractEmail(
  id: string,
  data: SendContractEmailData
): Promise<ApiResponse<SendContractEmailResult>> {
  return apiRequest<SendContractEmailResult>(`/contracts/${id}/send-email`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}
